import { useQuery } from '@apollo/client';
import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import authTokenUtil from '../../account/authTokenUtil';
import getRedirectQueryString from '../../account/getRedirectQueryString';
import localLogout from '../../account/localLogout';
import { gql } from '../../graphql/generated/gql';
import useShareSecret from '../../post/useShareSecret';
import useSlug from '../../utils/router/useSlug';
import { publicPostPath } from '../../utils/url/postPath';
import { publicTopicPath } from '../../utils/url/topicPath';
import { useIsNonVisitorUser } from '../../account/useIsLoggedIn';
import TryLogIn from './TryLogIn';

const PUBLIC_POST_REDIRECT = gql(`
  query PublicPostRedirect($id: ID!, $shareSecret: String) {
    publicPost(id: $id, shareSecret: $shareSecret) {
      id
      ...publicPostPath_post
    }
  }
`);

const PUBLIC_TOPIC_REDIRECT = gql(`
  query PublicTopicRedirect($id: ID!) {
    publicTopic(id: $id) {
      id
      ...publicTopicPath_topic
    }
  }
`);

interface EnsureUserProps {
  children?: JSX.Element;
}

const LogOut = () => {
  const loginUrl = `/login${getRedirectQueryString()}`;
  if (authTokenUtil.get()) {
    localLogout();
    return null;
  }
  return <Navigate to={loginUrl} replace />;
};

const TryPublicPost = () => {
  const { hash } = useLocation();
  const [id] = useSlug('postSlug');
  const shareSecret = useShareSecret();
  const { data, loading } = useQuery(PUBLIC_POST_REDIRECT, {
    variables: { id, shareSecret },
  });

  if (loading) return null;
  if (data?.publicPost?.id) {
    return (
      <Navigate
        to={publicPostPath(data.publicPost, {
          anchor: hash ? hash.replace('#', '') : undefined,
          shareSecret,
        })}
        replace
      />
    );
  }

  return <LogOut />;
};

const TryPublicTopic = () => {
  const [id] = useSlug('topicSlug');
  const { data, loading } = useQuery(PUBLIC_TOPIC_REDIRECT, {
    variables: { id },
  });

  if (loading) return null;
  if (data?.publicTopic?.id) {
    return <Navigate to={publicTopicPath(data.publicTopic)} replace />;
  }

  return <LogOut />;
};

const TryPublicContentRedirect = () => {
  const location = useLocation();
  const params = useParams();

  if (location.pathname.startsWith('/posts/') && params.postSlug) {
    return <TryPublicPost />;
  }

  if (location.pathname.startsWith('/topics/') && params.topicSlug) {
    return <TryPublicTopic />;
  }

  return <LogOut />;
};

const EnsureUser = ({ children }: EnsureUserProps) => {
  const isNonVisitorUser = useIsNonVisitorUser();
  return isNonVisitorUser ? (
    children || null
  ) : (
    <TryLogIn fallback={<TryPublicContentRedirect />} />
  );
};

export default EnsureUser;
